import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import color from "../utils/Colors";
import { getDataFromTable, insertDataInTable } from "../../services/services";
import { useUser } from "../../services/Context/UserContext";

const SkillsModal: React.FC<any> = ({
  open,
  setNewSkills,
  newSkills,
  handleClose,
  onSkillsSaved,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [skills, setSkills] = useState<any[]>([]);
  const [creating, setCreating] = useState(false); // ✅ loading state for create
  const { user } = useUser();
  useEffect(() => {
    if (open) {
      getDataFromTable("skills", {}).then((res) => {
        setSkills(res?.data?.data ?? []);
      });
    }
  }, [open]);

  const handleChipClick = (skill: any) => {
    if (newSkills.some((o: any) => o.title === skill.title)) {
      setNewSkills(newSkills.filter((o: any) => o.title !== skill.title));
    } else {
      setNewSkills([...newSkills, skill]);
    }
  };

  const handleCreateSkill = () => {
    if (!searchTerm.trim()) return;
    setCreating(true);

    insertDataInTable("skills", {
      title: searchTerm.trim(),
      createdBy: user?.id, // ✅ add user id
      status: "active", // ✅ add status
    })
      .then((res: any) => {
        const newSkill = res?.data?.data;
        if (newSkill) {
          setSkills((prev) => [...prev, newSkill]);
          setNewSkills([...newSkills, newSkill]);
          setSearchTerm("");
        }
      })
      .catch((err) => {
        console.error("Failed to create skill", err);
      })
      .finally(() => setCreating(false));
  };

  const filteredSkills = skills.filter((skill: any) =>
    skill.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ✅ Show create option only when search has value and no exact match
  const showCreateOption =
    searchTerm.trim().length > 0 &&
    !skills.some(
      (s: any) => s.title.toLowerCase() === searchTerm.trim().toLowerCase(),
    );

  return (
    <Modal open={open} onClose={handleClose}>
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "600px",
            bgcolor: "background.paper",
            boxShadow: 24,
            border: "6px solid",
            borderColor: "#c7b1e4",
            borderRadius: 2,
            backgroundColor: "white",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            color={color.textColor}
            style={{
              padding: "10px",
              paddingLeft: "15px",
              display: "flex",
              alignItems: "center",
              height: "70px",
              fontWeight: "bold",
              fontSize: "26px",
            }}
          >
            Add Skills
          </Typography>

          <div style={{ height: "50vh" }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                padding: "10px",
                paddingTop: "0px",
                paddingLeft: "15px",
              }}
            >
              <TextField
                placeholder="Skills (e.g., Data Analysis)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />

              {/* ✅ Create skill button — only shows when no match found */}
              {showCreateOption && (
                <Box
                  onClick={!creating ? handleCreateSkill : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: creating ? "not-allowed" : "pointer",
                    padding: "6px 12px",
                    borderRadius: "20px",
                    border: `1px dashed ${color.firstColor}`,
                    color: color.firstColor,
                    width: "100%",
                    opacity: creating ? 0.6 : 1,
                    "&:hover": { backgroundColor: "#f3eafd" },
                  }}
                >
                  <AddIcon fontSize="small" />
                  <Typography style={{ fontSize: "14px" }}>
                    {creating
                      ? "Creating..."
                      : `Create skill "${searchTerm.trim()}"`}
                  </Typography>
                </Box>
              )}

              <Typography>Suggested skills based on your profile</Typography>

              <div
                style={{ height: "200px", overflowY: "auto", width: "100%" }}
              >
                <div style={{ marginBottom: "10px" }}>
                  <Typography
                    variant="h6"
                    style={{
                      borderBottom: "1px solid #000",
                      marginBottom: "10px",
                      paddingLeft: "5px",
                      paddingBottom: "5px",
                    }}
                  >
                    All Skills
                  </Typography>
                  <div>
                    {filteredSkills.length === 0 && !showCreateOption ? (
                      <Typography
                        style={{
                          color: "gray",
                          fontSize: "14px",
                          padding: "5px",
                        }}
                      >
                        No skills found.
                      </Typography>
                    ) : (
                      filteredSkills.map((skill: any, index: number) => (
                        <Chip
                          key={index}
                          onClick={() => handleChipClick(skill)}
                          label={skill.title}
                          style={{
                            paddingLeft: "15px",
                            paddingRight: "15px",
                            margin: "4px",
                            cursor: "pointer",
                            backgroundColor: newSkills.some(
                              (o: any) => o.title === skill.title,
                            )
                              ? color.firstColor
                              : "white",
                            color: newSkills.some(
                              (o: any) => o.title === skill.title,
                            )
                              ? "white"
                              : color.firstColor,
                            border: "solid 1px",
                            borderColor: color.borderColor,
                          }}
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Box>
          </div>

          <div
            style={{
              padding: "4%",
              fontWeight: "bold",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button id="button" onClick={handleClose}>
              Back
            </Button>
            <Button
              id="button"
              style={{ background: color.firstColor, color: "white" }}
              onClick={() => {
                onSkillsSaved();
                handleClose();
              }}
            >
              Save
            </Button>
          </div>
        </Box>
      </div>
    </Modal>
  );
};

export default SkillsModal;
